'use client';

import React, { useLayoutEffect, useRef } from 'react';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { useTrackPublisherEvents } from '@/api/useTrackPublisherEvents';
import { ELocalStorageKeys, ELoginMethods } from '@/lib/enums';
import { EPortalRumEvents, logPortalRumError } from '@/lib/coralogix';
import { markPortalLandingAuth } from '@/lib/landingBeacon';
import { readLandingJourneyId } from '@/lib/landingJourney';
import { trackUrlOtpPublisherParityAtAuthStart } from '@/lib/urlOtpPublisherEvents';
import {
  EOtpAuthReasonCode,
  clearOtpUrlAuth,
  joinOtpCredentialAuth,
  shareOtpCredentialAuth,
} from '@/lib/otpAuth';
import type { IServerOtpLogin } from '@/lib/serverOtpLogin';
import { appStateService } from '@/state/app.state.service';
import { useAppState } from '@/state/hooks/app.state.hook';
import { useLoginState } from '@/state/hooks/login.state.hook';
import { webStorageUtil } from '@/state/webStorage.state.service';

import {
  captureOtpLandingFromWindow,
  fetchPortalBoot,
  isPortalBootReady,
  settleServerOtpLogin,
} from './OtpCredentialAuthController.utils';

interface IOtpCredentialAuthControllerProps {
  bootRoute: string;
  onSettled: () => void;
  /** The login the server is performing for this landing; adopted instead of sending our own once it arrives. */
  serverOtpLogin?: Promise<IServerOtpLogin | undefined>;
}

export const OtpCredentialAuthController = ({
  bootRoute,
  onSettled,
  serverOtpLogin,
}: IOtpCredentialAuthControllerProps): React.ReactElement | null => {
  const router = useRouter();
  const t = useTranslations();
  const { platform } = useAppState();
  const { trackPublisherPreLoginEvent, trackPublisherUIEvent } = useTrackPublisherEvents();
  const { loginResponse, setIsOtpLoggingIn, setIsLoginModalOpen } = useLoginState();
  const startedRef = useRef(false);
  const settledRef = useRef(false);

  useLayoutEffect(() => {
    const markSettled = (): void => {
      if (settledRef.current) {
        return;
      }
      settledRef.current = true;
      onSettled();
    };

    const settleFailedAuth = (): void => {
      clearOtpUrlAuth();
      setIsOtpLoggingIn(false);
      markSettled();
    };

    if (startedRef.current) {
      return;
    }

    const credentials = captureOtpLandingFromWindow();
    if (!credentials) {
      const inFlightAuth = joinOtpCredentialAuth();
      if (inFlightAuth) {
        void inFlightAuth.finally(() => {
          markSettled();
        });
        return;
      }

      settleFailedAuth();
      return;
    }
    startedRef.current = true;

    setIsLoginModalOpen(false);
    setIsOtpLoggingIn(true);
    // Read by the pre-bundle reporter on every report it sends, so a page left
    // while this still says pending is a login that hung or threw — the reporter
    // cannot otherwise tell that from a player who would not wait.
    markPortalLandingAuth('pending');

    const failWithoutLogin = (reasonCode: string): void => {
      markPortalLandingAuth('failed');
      logPortalRumError(EPortalRumEvents.PORTAL_LOGIN_FAILED, {
        method: ELoginMethods.OTP,
        reasonCode,
        pathname: window.location.pathname,
        appSessionId: webStorageUtil.getStorageWithFallback()?.id,
        journeyId: readLandingJourneyId(),
      });
      router.replace(
        `/failed?error=auth&message=${encodeURIComponent(
          t('portal.failed.authenticationFailed')
        )}&authMethod=otp`
      );
      settleFailedAuth();
    };

    const waitForBoot = async (): Promise<boolean> => {
      const initialBoot = appStateService.initialBoot.get();
      if (isPortalBootReady(initialBoot)) {
        return true;
      }

      try {
        const refreshedBoot = await fetchPortalBoot(bootRoute);
        appStateService.setInitialBoot(refreshedBoot);
        return isPortalBootReady(refreshedBoot);
      } catch {
        return false;
      }
    };

    const authenticate = async (): Promise<void> => {
      const bootReady = await waitForBoot();
      if (!bootReady) {
        failWithoutLogin(EOtpAuthReasonCode.BOOT_FAILED);
        return;
      }

      trackUrlOtpPublisherParityAtAuthStart({
        platform,
        pathname: window.location.pathname,
        pages: appStateService.initialBoot.get()?.pages,
        playerId: webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)?.playerId,
        trackers: { trackPublisherPreLoginEvent, trackPublisherUIEvent },
      });

      try {
        await shareOtpCredentialAuth(async () => {
          const serverLogin = await settleServerOtpLogin(serverOtpLogin);
          const succeeded = await loginResponse({
            authMethod: ELoginMethods.OTP,
            otp: {
              playerCode: credentials.proofKey,
              accessToken: credentials.token,
            },
          }, true, serverLogin);
          markPortalLandingAuth(succeeded ? 'ok' : 'failed');
          if (!succeeded) {
            clearOtpUrlAuth();
            setIsOtpLoggingIn(false);
          }
        });
      } finally {
        markSettled();
      }
    };

    void authenticate();
  }, [
    bootRoute,
    loginResponse,
    onSettled,
    platform,
    router,
    serverOtpLogin,
    setIsLoginModalOpen,
    setIsOtpLoggingIn,
    t,
    trackPublisherPreLoginEvent,
    trackPublisherUIEvent,
  ]);

  return null;
};
